import { useCallback, useSyncExternalStore } from "react";

const APP_NAME = "MyFleet";

/** 是否為已安裝的 PWA（standalone / fullscreen / minimal-ui，或 iOS 主畫面） */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  if (typeof window.navigator !== "undefined" && window.navigator.standalone === true)
    return true;
  if (typeof window.matchMedia === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches
  );
}

/** 是否為 iOS（Safari 需用「分享 → 加入主畫面」） */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/**
 * 是否為 iOS 上的非 Safari 環境（第三方瀏覽器或 App 內建 webview）。
 *
 * iOS 只有 Safari 能建立真正的 standalone 網頁 App；其他瀏覽器的「加入主畫面」多半只是
 * 捷徑，display-mode 不會是 standalone，於是 iOS Web Push 直接收不到。所以這些環境要導去
 * Safari，不能給安裝步驟。
 *
 * 注意：已安裝的 standalone 視窗 UA 也沒有 Safari/ token，會誤判為 true，
 * 所以呼叫端必須先用 isStandalone() 擋掉。
 */
export function isIOSNonSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  // 第三方瀏覽器各有自己的標記
  if (/CriOS|EdgiOS|FxiOS|OPiOS|OPT\/|YaBrowser|DuckDuckGo/.test(ua)) return true;
  // App 內建 webview（Facebook / LINE / Gmail 等）：真 Safari 一定有 Safari/ token
  return !/Safari\//.test(ua);
}

/** 是否為 Android（Chrome 選單可安裝） */
export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/.test(navigator.userAgent);
}

/** 取得目前 display-mode（用於除錯） */
export function getDisplayMode() {
  if (typeof window === "undefined") return "unknown";
  if (window.matchMedia("(display-mode: standalone)").matches)
    return "standalone";
  if (window.matchMedia("(display-mode: fullscreen)").matches)
    return "fullscreen";
  if (window.matchMedia("(display-mode: minimal-ui)").matches)
    return "minimal-ui";
  return "browser";
}

/*
 * beforeinstallprompt 在頁面載入後只觸發一次且不會重播，所以必須在模組層就接住：
 * 若等到元件 mount 才 addEventListener，使用者在 SPA 內導航到別頁時事件早已過去，
 * 永遠拿不到 prompt。main.jsx 有一行 side-effect import 確保這段在 React 掛載前執行。
 */
let deferredPrompt = null;
let installed = false;
const subscribers = new Set();
const notify = () => subscribers.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

const subscribe = (cb) => {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};
// 回傳字串而非事件物件，useSyncExternalStore 才能靠值相等判斷是否要重繪
const getSnapshot = () => (installed ? "installed" : deferredPrompt ? "ready" : "none");
const getServerSnapshot = () => "none";

/**
 * PWA 安裝狀態與一鍵安裝。
 *
 * canInstall 為真時（Android Chromium 系）呼叫 install() 會跳出系統原生安裝對話框，
 * 使用者按一下就完成，不需要任何操作說明。iOS 沒有這個 API，canInstall 永遠是 false。
 *
 * @returns {{
 *   canInstall: boolean,
 *   installed: boolean,
 *   install: () => Promise<"accepted" | "dismissed" | "unavailable" | "error">,
 *   appName: string,
 * }}
 */
export function useInstallPWA() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const install = useCallback(async () => {
    if (!deferredPrompt) return "unavailable";
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        deferredPrompt = null;
        notify();
      }
      return outcome;
    } catch (err) {
      console.error("[install] prompt() 失敗", err);
      return "error";
    }
  }, []);

  return {
    canInstall: state === "ready",
    installed: state === "installed",
    install,
    appName: APP_NAME,
  };
}
